import React from 'react'
import {withRouter} from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import {HostedLoginService, GoogleAnalyticsService, GameService, CaptchaService} from 'services'
import {withNotifications} from 'hocs'
import {NOTIFICATION_TYPES} from 'const/notifications'
import {compose} from 'utils/reactUtils'
import {getParamFromQuery} from 'utils/stringUtils'
import {
  getErrorMessage,
  InvalidUserPassword,
  InvalidNativeValidLegacyPassword,
  UserNotExist,
  getChangePasswordErrorMessage,
} from 'utils/errorUtils'
import {FormContainer} from 'components/form'
import Loading from 'components/loading'
import {reCaptchaValidator} from 'const/validations'
import SecurityPasswordReset from '../security-password-reset'
import {InteractionContext} from 'context/InteractionContext'

class LoginFormContainer extends React.Component {
  gameService = new GameService()

  constructor(props) {
    super(props)

    this.state = {
      nativeLoginError: null,
      loading: false,
      loginLoading: false,
      gameConfig: {},
      shouldShowCaptcha: false,
      isVerifiedByReCaptcha: false,
      isSecurityResetPassword: false,
      email: '',
    }
  }

  componentDidMount() {
    this.init()
  }

  checkCaptcha = async email => {
    try {
      await CaptchaService.checkCaptchaV3({email, action: 'login'})
    } catch (e) {
      if (e.status === 400 && e.message === 'captcha required') {
        this.setState({shouldShowCaptcha: true})
      }
    }
  }

  init = async () => {
    const loginHint = getParamFromQuery('login_hint', window.location.search)
    const loginMethods = getParamFromQuery('login_methods', window.location.search)

    if (loginHint && loginMethods) {
      const loginMethodsSplitted = loginMethods.split(',')
      const isHintedLogin = Array.isArray(loginMethodsSplitted) && loginMethodsSplitted.length > 0
      this.setState({isHintedLogin})
    }

    try {
      if (this.props.email) {
        await this.checkCaptcha(this.props.email)
        this.setState({isVerifiedByReCaptcha: true})
      }
      this.gameService.fetchGameConfig().then(gameConfig => {
        if (!isEmpty(gameConfig)) {
          this.setState({gameConfig: {...gameConfig.landing, image: gameConfig.image}})
        }
      })
    } catch (e) {
      // do nothing
    }
  }

  loginWithCredentials = async data => {
    // prevent from sending request several times
    if (!this.state.loginLoading) {
      try {
        this.setState({loginLoading: true})
        GoogleAnalyticsService.trackLoginStart()
        const {existsInNative, existsInLegacy} = await HostedLoginService.userExists(
          data.email,
          this.context.interactionToken
        )
        if (existsInNative || existsInLegacy) {
          const bid = await HostedLoginService.login(data, this.context.loginCase)
          GoogleAnalyticsService.trackLoginSuccess(bid)
        } else {
          this.setState({
            UserNotExistError: React.createElement(UserNotExist),
            loginLoading: false,
          })
        }
      } catch (e) {
        GoogleAnalyticsService.trackLoginError('native', e.message)
        if (e.status === 401 && e.message === 'User should use native password') {
          this.setState({
            nativeLoginError: React.createElement(InvalidNativeValidLegacyPassword, {
              onForgetPassword: this.handleForgetPassword(data.email),
            }),
          })
        } else if (e.status === 401 || e.status === 422) {
          this.setState({
            nativeLoginError: React.createElement(InvalidUserPassword, {
              onForgetPassword: this.handleForgetPassword(data.email),
            }),
          })
        } else if (e.status === 400 && e.message === 'captcha required') {
          this.setState({shouldShowCaptcha: true})
        } else if (e.status === 400 && e.message === 'invalid captcha') {
          this.props.showNotification(NOTIFICATION_TYPES.error, 'notification.captcha-error')
        } else if (e.status === 403) {
          this.setState({isSecurityResetPassword: true, email: data.email})
        } else {
          this.props.showNotification(NOTIFICATION_TYPES.error, getErrorMessage(e))
        }
        this.setState({loginLoading: false})
      }
    }
  }

  handleForgetPassword = email => async () => {
    this.setState({loading: true})
    try {
      await HostedLoginService.forgetPassword({email})
      GoogleAnalyticsService.trackForgotPassword()
      this.props.history.push(`/forget?email=${encodeURIComponent(email)}`)
    } catch (e) {
      if (e.status === 400 && e.message === 'captcha required') {
        return this.props.history.push(`/forget?email=${encodeURIComponent(email)}&captcha=true&page=login`)
      }
      const message = getChangePasswordErrorMessage(e)
      this.setState({loading: false})
      this.props.showNotification(NOTIFICATION_TYPES.error, message)
    }
  }

  handleEmailChange = email => {
    this.props.history.push(`/?email=${encodeURIComponent(email)}`)
  }

  resetNativeError = () => this.setState({nativeLoginError: null, UserNotExistError: null})

  getValidations = () => {
    const extraValidator = this.state.shouldShowCaptcha ? [reCaptchaValidator] : []
    return [...this.props.validations, ...extraValidator]
  }

  render() {
    const {email, children} = this.props
    const {loading, loginLoading, nativeLoginError, gameConfig, isHintedLogin, UserNotExistError} = this.state
    const customErrors = {
      ...(nativeLoginError && {password: nativeLoginError}),
      ...(UserNotExistError && {email: UserNotExistError}),
    }

    if (loading) {
      return <Loading />
    }

    const content = this.state.isSecurityResetPassword ? (
      <SecurityPasswordReset email={this.state.email} loginCase={this.loginCase} />
    ) : (
      React.isValidElement(children) &&
      React.cloneElement(children, {
        email,
        loading: loginLoading,
        nativeLoginError,
        onEmailChange: this.handleEmailChange,
        onEmailBlur: this.checkCaptcha,
        onForgetPassword: this.handleForgetPassword(email),
        loginCase: this.context.loginCase,
        gameConfig,
        isHintedLogin,
        shouldShowCaptcha: this.state.shouldShowCaptcha,
      })
    )

    return (
      <FormContainer
        onSubmit={this.loginWithCredentials}
        customErrors={customErrors}
        resetCustomErrors={this.resetNativeError}
        data={{email, password: ''}}
        isExtraValid={this.props.email ? this.state.isVerifiedByReCaptcha : true}
        validations={this.getValidations()}>
        {content}
      </FormContainer>
    )
  }
}

LoginFormContainer.contextType = InteractionContext

export default compose(withNotifications(), withRouter)(LoginFormContainer)
